import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeriesItemTooltip,
  ChartTooltip,
  ChartArea,
  ChartValueAxis,
  ChartValueAxisItem
} from '@progress/kendo-react-charts';
import 'hammerjs';
import '@progress/kendo-theme-material/dist/all.css';
import { getDateToString, getDateTimeFromAPIDate } from 'src/utils/date';
import {
  CurrencyNumberFormatString,
  EnergyNumberFormatString
} from 'src/utils/numbers';
import { COLORS } from 'src/constants';
import moment from 'moment';
import "moment/locale/sv"; 

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0)
    }
  },
  label: {
    marginLeft: theme.spacing(1)
  },
  tooltip: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize
  }
}));

function ElectricityConsumption({ consumption, className, comparisonConsumption, resolution, isAverage = false, ...rest }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!consumption) {
    return null;
  }

  //const labelContent = e => isMobile ? "" : resolution === 5 ? getDateTimeFromAPIDate(e.value).toLocaleTimeString() : getDateToString(getDateTimeFromAPIDate(e.value));
  const labelContent = e => {
    if(isMobile){
      return "";
    }
    const dateObj = getDateTimeFromAPIDate(e.value);

    if (resolution === 5) {
      return dateObj.toLocaleTimeString();
    }

    if (resolution === 3) {
      let date = moment(dateObj).locale("sv").format("MMMM YYYY");
      // Gör första bokstaven stor
      return date.charAt(0).toUpperCase() + date.slice(1);
    }

    return getDateToString(dateObj);
  };
  
  const Tooltip = ({ point }) => {
    const priceSum = point.dataItem.electricity.priceSum;
    const absPriceSum = Math.abs(priceSum);
    const label = priceSum === 0 ? "" : priceSum < 0 ? "Att betala" : "Att få tillbaks";

    return (
      <span className={classes.tooltip}>
        <b>El</b>{' '}
        {resolution === 5 ? getDateTimeFromAPIDate(point.dataItem.readDate).toLocaleTimeString() : getDateToString(getDateTimeFromAPIDate(point.dataItem.readDate))}
        <br />
        {EnergyNumberFormatString(point.value)}
        {(resolution !== 5 && !isAverage) &&
        <>
        <br />
        {label}: {CurrencyNumberFormatString(absPriceSum)}
        </>
        }
      </span>
    );
  };

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <Box flexGrow={1}>
        <Typography
          component="h3"
          gutterBottom
          variant="overline"
          color="textSecondary"
          sx={{mt: isMobile ? theme.spacing(2) : 0, ml: isMobile ? theme.spacing(2) : 0}}
        >
          Elförbrukning
        </Typography>

        <Chart>
          <ChartTooltip />
          <ChartArea background={theme.palette.background.paper} />
          <ChartValueAxis>
            <ChartValueAxisItem
              labels={{ color: theme.palette.text.primary }}
            />
          </ChartValueAxis>
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              labels={{
                content: labelContent,
                rotation: 'auto',
                color: theme.palette.text.primary
              }}
              categories={consumption.periods.map(p => p.readDate)}
            ></ChartCategoryAxisItem>
          </ChartCategoryAxis>
          <ChartSeries>
            <ChartSeriesItem
              data={consumption.periods}
              field="electricity.value"
              //categoryField="readDate"
              stack={false}
              type="column"
              color={COLORS.ELECTRICITY}
            >
              <ChartSeriesItemTooltip render={Tooltip} />
            </ChartSeriesItem>
            {comparisonConsumption && (
              <ChartSeriesItem
                data={comparisonConsumption.periods}
                field="electricity.value"
                //categoryField="readDate"
                type="column"
                stack={false}
                color={COLORS.ELECTRICITY_COMPARE || "#FF6384"} // ny färg
              >
                <ChartSeriesItemTooltip render={Tooltip} />
              </ChartSeriesItem>
            )}
          </ChartSeries>
        </Chart>
      </Box>
    </Card>
  );
}

ElectricityConsumption.propTypes = {
  className: PropTypes.string
};

export default ElectricityConsumption;
